import { useState, useEffect, useCallback } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useMutation } from '@tanstack/react-query';
import { AssumptionsForm } from '@/components/forms/AssumptionsForm';
import { ResultsTabs } from '@/components/results/ResultsTabs';
import { EmailGateModal, getStoredEmail, getStoredName } from '@/components/modals/EmailGateModal';
import { getModelConfig, MODEL_TYPES, MODEL_REGISTRY } from '@/config/models';
import type { Assumptions, ModelData } from '@/types/models';
import { api } from '@/services/api';

/**
 * ModelPage - Dynamic model page for all 10 business types
 * Handles assumptions input, API calculation, and results display
 * Now with mandatory email capture before generate/download
 */
export function ModelPage() {
  const { modelType: paramModelType } = useParams<{ modelType: string }>();
  const location = useLocation();

  // Determine model type from URL
  const modelType = paramModelType || extractModelTypeFromPath(location.pathname);
  const config = getModelConfig(modelType || 'saas');
  const modelInfo = MODEL_TYPES.find((m) => m.id === modelType) || MODEL_TYPES[0];

  // State
  const [assumptions, setAssumptions] = useState<Assumptions | null>(null);
  const [results, setResults] = useState<ModelData | null>(null);
  const [activeTab, setActiveTab] = useState('revenue');
  const [showResults, setShowResults] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Email Gate State
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [pendingAction, setPendingAction] = useState<'generate' | 'download' | null>(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);

  // Load default assumptions and check for stored email on mount
  useEffect(() => {
    if (config) {
      // Get defaults from the config
      const defaults: Assumptions = {};
      config.assumptionGroups.forEach((group) => {
        group.fields.forEach((field) => {
          if (field.defaultValue !== undefined) {
            defaults[field.id] = field.defaultValue;
          }
        });
      });
      setAssumptions(defaults);
    }

    // Check for stored email
    const storedEmail = getStoredEmail();
    if (storedEmail) {
      setUserEmail(storedEmail);
    }
  }, [config, modelType]);

  // Capture lead in backend
  const captureLeadMutation = useMutation({
    mutationFn: async ({ email, name, source }: { email: string; name?: string; source: string }) => {
      try {
        await fetch('/auth/lead', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, name, source }),
        });
      } catch (e) {
        // Fail silently - don't block user
        console.error('Lead capture error:', e);
      }
    },
  });

  // Calculate mutation
  const calculateMutation = useMutation({
    mutationFn: async (data: Assumptions) => {
      // Different APIs expect different formats:
      // - SaaS: { assumptions: {...} }
      // - All others: assumptions directly in body
      const isSaaS = modelType === 'saas';
      const requestBody = isSaaS ? { assumptions: data } : data;

      console.log(`📊 Calculating ${modelType} model with:`, requestBody);

      const response = await api.post<{ success: boolean; data: ModelData; error?: string }>(
        `${config?.apiEndpoint}/calculate`,
        requestBody
      );
      if (!response.success) {
        throw new Error(response.error || 'Calculation failed');
      }
      return response.data;
    },
    onSuccess: (data) => {
      console.log('API Response:', data);
      setResults(data);
      setShowResults(true);
      // Scroll to results
      setTimeout(() => {
        document.getElementById('results-section')?.scrollIntoView({
          behavior: 'smooth',
        });
      }, 100);
    },
    onError: (error: Error) => {
      console.error('Calculation error:', error);
      alert(`Error calculating model: ${error.message || 'Please check your inputs and try again.'}`);
    },
  });

  // Handle email submission from modal
  const handleEmailSubmit = useCallback(
    async (email: string, name?: string) => {
      setUserEmail(email);

      // Capture lead with model type and action
      const source = `${modelType}_${pendingAction}`;
      captureLeadMutation.mutate({ email, name, source });

      // Close modal
      setShowEmailModal(false);

      // Execute pending action
      if (pendingAction === 'generate' && assumptions) {
        calculateMutation.mutate(assumptions);
      } else if (pendingAction === 'download') {
        await executeDownload();
      }

      setPendingAction(null);
    },
    [assumptions, captureLeadMutation, calculateMutation, pendingAction, modelType]
  );

  // Handle generate model - now with email gate
  const handleGenerateModel = useCallback(() => {
    if (!assumptions) return;

    if (!userEmail) {
      setPendingAction('generate');
      setShowEmailModal(true);
    } else {
      // Re-capture lead with updated source on each generate
      captureLeadMutation.mutate({
        email: userEmail,
        name: getStoredName() || undefined,
        source: `${modelType}_generate`,
      });
      calculateMutation.mutate(assumptions);
    }
  }, [assumptions, userEmail, calculateMutation, captureLeadMutation, modelType]);

  // Execute download (after email is captured)
  const executeDownload = useCallback(async () => {
    if (!assumptions || !config) return;

    setIsDownloading(true);
    try {
      // Different models have different Excel endpoints and payload formats
      let endpoint: string;
      let requestBody: unknown;

      switch (modelType) {
        case 'saas':
          endpoint = '/api/saas/excel';
          requestBody = { assumptions, modelData: results };
          break;
        case 'ecommerce':
          endpoint = '/api/ecommerce/excel';
          requestBody = { modelData: results, assumptions };
          break;
        case 'cloudkitchen':
          endpoint = '/api/cloudkitchen/excel-v2';
          requestBody = assumptions;
          break;
        default:
          endpoint = `/api/${modelType}/export`;
          requestBody = assumptions;
          break;
      }

      console.log(`📥 Downloading Excel for ${modelType} from ${endpoint}...`);

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Excel API error:', response.status, errorText);
        throw new Error(`Failed to generate Excel: ${response.status}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${modelType}-financial-model.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      console.log('✅ Excel downloaded successfully');
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Please try again.';
      console.error('Download error:', error);
      alert(`Error downloading Excel file: ${errorMessage}`);
    } finally {
      setIsDownloading(false);
    }
  }, [assumptions, config, modelType, results]);

  // Handle Excel download - with email gate
  const handleDownloadExcel = useCallback(async () => {
    if (!assumptions || !config || !results) {
      alert('Please generate the model first before downloading Excel.');
      return;
    }

    if (!userEmail) {
      setPendingAction('download');
      setShowEmailModal(true);
    } else {
      // Re-capture lead with download source
      captureLeadMutation.mutate({
        email: userEmail,
        name: getStoredName() || undefined,
        source: `${modelType}_download`,
      });
      await executeDownload();
    }
  }, [assumptions, config, results, userEmail, captureLeadMutation, modelType, executeDownload]);

  if (!config) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="text-6xl mb-4">🔍</div>
          <h1 className="text-xl md:text-2xl font-bold text-slate-900 mb-2">
            Model Not Found
          </h1>
          <p className="text-slate-600 mb-6">
            The requested model type does not exist.
          </p>
          <Link
            to="/dashboard"
            className="text-primary-600 font-semibold hover:underline"
          >
            Browse All Models
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 font-sans antialiased">
      {/* Email Gate Modal */}
      <EmailGateModal
        isOpen={showEmailModal}
        onClose={() => {
          setShowEmailModal(false);
          setPendingAction(null);
        }}
        onSubmit={handleEmailSubmit}
        action={pendingAction || 'generate'}
        modelType={modelType || 'saas'}
        isLoading={calculateMutation.isPending || isDownloading}
      />

      {/* Navigation - Mobile Responsive */}
      <nav className="sticky top-0 z-40 bg-slate-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14 md:h-16">
            <Link to="/" className="flex items-center gap-2">
              <img
                src="/images/logo.png"
                alt="FinModel"
                className="h-6 md:h-8 w-auto"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
              <span className="text-lg md:text-xl font-semibold">FinModel</span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-6">
              <Link
                to="/dashboard"
                className="text-slate-300 hover:text-white transition-colors"
              >
                All Models
              </Link>
              <Link
                to="/features"
                className="text-slate-300 hover:text-white transition-colors"
              >
                Features
              </Link>
              <a
                href="#"
                className="text-slate-300 hover:text-white transition-colors"
              >
                Contact
              </a>
              <span
                className="bg-white/20 px-3 py-1.5 rounded-full text-sm font-medium"
                style={{ backgroundColor: `${modelInfo.color}30` }}
              >
                {modelInfo.icon} {config.displayName}
              </span>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2 rounded-lg hover:bg-white/10 transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {mobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>

          {/* Mobile Menu */}
          <AnimatePresence>
            {mobileMenuOpen && (
              <motion.div
                className="md:hidden border-t border-slate-700 py-3"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
              >
                <div className="flex flex-col space-y-3">
                  <Link
                    to="/dashboard"
                    className="text-slate-300 hover:text-white transition-colors py-2"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    All Models
                  </Link>
                  <Link
                    to="/features"
                    className="text-slate-300 hover:text-white transition-colors py-2"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Features
                  </Link>
                  <a
                    href="#"
                    className="text-slate-300 hover:text-white transition-colors py-2"
                  >
                    Contact
                  </a>
                  <div
                    className="inline-flex w-fit bg-white/20 px-3 py-1.5 rounded-full text-sm font-medium"
                    style={{ backgroundColor: `${modelInfo.color}30` }}
                  >
                    {modelInfo.icon} {config.displayName} Model
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 md:py-8">
        {/* Assumptions Section */}
        <motion.div
          className="bg-white rounded-xl shadow-lg p-4 md:p-8 mb-6 md:mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Header - Mobile Responsive */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6 md:mb-8">
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-slate-900 flex items-center gap-3">
                <span
                  className="w-8 h-8 md:w-10 md:h-10 rounded-lg flex items-center justify-center text-lg md:text-xl shrink-0"
                  style={{ backgroundColor: `${modelInfo.color}15` }}
                >
                  {modelInfo.icon}
                </span>
                <span className="leading-tight">{config.displayName} Assumptions</span>
              </h1>
              <p className="text-slate-600 mt-2 text-sm md:text-base">{config.description}</p>
            </div>
            <Link
              to="/dashboard"
              className="text-slate-500 hover:text-slate-700 text-sm flex items-center gap-1 self-start md:self-auto"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              Change Model
            </Link>
          </div>

          <AssumptionsForm
            groups={config.assumptionGroups}
            values={assumptions}
            onChange={setAssumptions}
            isLoading={!assumptions}
          />

          {/* Generate Button */}
          <div className="mt-6 md:mt-8 flex justify-center">
            <motion.button
              onClick={handleGenerateModel}
              disabled={calculateMutation.isPending}
              className="w-full md:w-auto bg-primary-600 text-white px-6 md:px-8 py-3.5 md:py-4 rounded-lg font-semibold hover:bg-primary-700 transition-all shadow-lg hover:shadow-primary-500/25 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {calculateMutation.isPending ? (
                <>
                  <svg
                    className="animate-spin w-5 h-5"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Calculating...
                </>
              ) : (
                <>
                  🚀 Generate Financial Model
                </>
              )}
            </motion.button>
          </div>
        </motion.div>

        {/* Results Section */}
        <AnimatePresence>
          {showResults && results && (
            <motion.div
              id="results-section"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <ResultsTabs
                modelData={results}
                modelType={modelType || 'saas'}
                tabs={config.resultTabs}
                activeTab={activeTab}
                onTabChange={setActiveTab}
                onDownloadExcel={handleDownloadExcel}
                isDownloading={isDownloading}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

/**
 * Extract model type from legacy URL paths like /saas.html or /saas
 */
function extractModelTypeFromPath(pathname: string): string {
  const modelTypes = Object.keys(MODEL_REGISTRY);
  const path = pathname.toLowerCase().replace('.html', '').replace('/', '');

  for (const type of modelTypes) {
    if (path === type || path.endsWith(type)) {
      return type;
    }
  }

  return 'saas'; // default
}
